<template>
  <v-container
    grid-list-xl
    fluid
  >
    <translations-tabs v-if="initialized">
      <template v-slot:default="{ language }">
        <v-layout
          row
          wrap
          class="pt-2"
        >
          <v-flex
            xs12
            md4>
            <translatable
              v-model="page"
              :language="language"
              field-name="name"
            >
              <template #default="{ fieldValue, fieldMutator }">
                <v-text-field
                  v-model="fieldValue"
                  :error-messages="verrors('name')"
                  :disabled="page.protected"
                  :append-icon="language && language.default ? 'loop': ''"
                  label="Name"
                  persistent-hint
                  outline
                  @change="fieldMutator(fieldValue)"
                  @click:append="regenerateSlug"
                />
              </template>
            </translatable>
          </v-flex>

          <v-flex
            xs12
            md4>
            <v-text-field
              v-model="page.slug"
              :disabled="language && !language.default"
              :error-messages="verrors('slug')"
              label="Slug"
              persistent-hint
              outline
            />
          </v-flex>

          <v-flex
            xs12
            md4>
            <v-select
              v-model="page.type"
              :disabled="language && !language.default"
              :items="['raw_html', 'json']"
              label="Type"
              outline
            />
          </v-flex>
        </v-layout>

        <v-layout
          row
          wrap
        >
          <v-flex xs12>
            <div class="label my-4">
              <span class="font-weight-medium">{{ $t('Content') }}:</span>
            </div>
            <translatable
              v-if="page && language"
              v-model="page"
              :language="language"
              :key="language.code"
              field-name="content"
            >
              <template #default="{ fieldValue, fieldMutator, dynamicFieldValue }">
                <monaco-editor
                  :theme="$vuetify.dark ? 'vs-dark' : 'vs-light'"
                  :content="fieldValue"
                  v-model="dynamicFieldValue"
                  :options="monacoOptions"
                  :language="page.type === 'json' ? 'json' : 'html'"
                  class="editor"
                  @change="fieldMutator(dynamicFieldValue)"
                />
              </template>
            </translatable>
          </v-flex>
        </v-layout>
      </template>
    </translations-tabs>
    <v-flex
      v-if="page.fields"
      class="mt-4"
      xs12>
      <div class="label">
        <span class="font-weight-medium">{{ $t('Available variables') }}:</span>
      </div>

      <ul class="mb-3">
        <li
          v-for="(field, name) in page.fields"
          :key="name">
          <span><b>{{ name }}</b> - {{ field.description }}</span>
          <span class="mx-3">{{ field.types }}</span>
        </li>
      </ul>

      <span class="font-weight-light">The HTML and Scripts fields support the <a href="https://mustache.github.io/mustache.5.html">mustache template syntax</a>.</span>
    </v-flex>

    <v-flex xs12>
      <div class="label my-4">
        <span class="font-weight-medium">{{ $t('Scripts') }}:</span>
      </div>
      <monaco-editor
        v-if="page"
        v-model="page.scripts"
        :options="monacoOptions"
        class="editor"
        language="javascript"
      />
    </v-flex>
  </v-container>
</template>

<script>
import MonacoEditor from 'vue-monaco'
import TranslationsTabs from '@/components/widgets/TranslationsTabs'
import Translatable from '@/components/widgets/Translatable'

export default {
  name: 'PageSingle',

  components: {
    Translatable,
    TranslationsTabs,
    MonacoEditor
  },

  actionBar () {
    return {
      title: 'Page',
      actions: [
        {
          text: 'Save',
          event: 'save'
        },
        {
          text: 'Delete',
          event: 'delete'
        }
      ]
    }
  },

  data () {
    return {
      page: {
        content: '',
        name: '',
        slug: '',
        translations: null,
        scripts: ''
      },
      loading: false,
      initialized: false,
      monacoOptions: {
        minimap: {
          enabled: false
        }
      }
    }
  },

  computed: {
    verrors () {
      return field => {
        if (!this.page.errors) {
          return []
        }
        return this.page.errors
          .filter(obj => obj.field === field)
          .map(obj => obj.message)
          .flat()
      }
    },

    saveActionStates () {
      return {
        disabled: this.page.errors && this.page.errors.length,
        loading: this.loading
      }
    },

    deleteActionStates () {
      return {
        disabled: (!this.page.id) || (this.page.id && this.page.protected),
        loading: this.loading
      }
    }
  },

  watch: {
    'page.name': function (value) {
      if (!value || this.page.id) {
        return
      }

      this.page.slug = this.generateSlug(value)
    }
  },

  async mounted () {
    this.loading = true
    if (this.$route.params.id) {
      this.page = await this.$http.make('page').find(this.$route.params.id, {
        headers: { 'x-template-code': this.$route.query.code }
      })
    } else {
      this.page = await this.$http.make('page', {
        headers: { 'x-template-code': this.$route.query.code }
      })
    }

    this.loading = false
    this.initialized = true
  },

  methods: {
    regenerateSlug () {
      this.page.slug = this.generateSlug(this.page.name)
    },

    generateSlug (value) {
      return (value || '').toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '')
    },

    onActionSave () {
      const code = this.$route.query.code
      this.loading = true
      this.page.save({ headers: { 'x-template-code': code } }).then(() => {
        this.$toast.success(this.$t('Page saved successfully'))
      }).finally(() => {
        this.loading = false
      })
    },

    onActionDelete () {
      const code = this.$route.query.code
      this.loading = true
      this.page.delete({ headers: { 'x-template-code': code } }).then(() => {
        this.$toast.success(this.$t('Page deleted successfully'))
        this.$router.push({ name: 'pages' })
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style>
.editor {
  width: 100%;
  height: 400px;
}
</style>
